import { Box, Gap, P, SvgIcon, theme } from '@salecycle/ui-component-library';
import { WorkflowBox } from '../../WorkflowBox/WorkflowBox';
import { WorkflowBoxHeader } from '../../WorkflowBox/WorkflowBoxHeader';
import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { WorkflowLabel } from '../../WorkflowBox/WorkflowLabel';
import styled from 'styled-components';
import { SendEmailThumbnail } from './SendEmailThumbnail';
import { useWorkflowForm } from '../../../contexts/CampaignWorkflowFormContext';
import { WorkflowContent } from '../../WorkflowBox/WorkflowContent';
import { useSendEmail } from './hooks/useSendEmail';
import { useDrawer } from '../../../state/Drawer/hooks/useDrawer';
import {
  SendEmailRequiredConfigRule,
  SubjectLineRequiredRule,
  SendEmailTemplateRequiredRule,
} from 'sections/onsite-campaigns/pages/CampaignEditor/domain/Workflow/Rules';
import { WorkflowBoxFooter } from '../../WorkflowBox/WorkflowBoxFooter';

const useShouldShowMissingFieldsWarning = id => {
  const { getIsFocused, getIsTouched } = useWorkflowForm();
  const { getSubjectLine, getTemplate, getSendEmail } = useSendEmail();

  const subjectLine = useMemo(() => getSubjectLine(id), [getSubjectLine, id]);
  const template = useMemo(() => getTemplate(id), [getTemplate, id]);
  const sendEmail = useMemo(() => getSendEmail(id), [getSendEmail, id]);

  return useMemo(() => {
    return (
      (SendEmailRequiredConfigRule(sendEmail).result === 'fail' ||
        SubjectLineRequiredRule({ subjectLine }).result === 'fail' ||
        SendEmailTemplateRequiredRule({ template }).result === 'fail') &&
      !getIsFocused(id) &&
      getIsTouched(id)
    );
  }, [sendEmail, subjectLine, template, getIsFocused, id, getIsTouched]);
};

const SelectedContent = styled.div`
  display: flex;
`;

const Grow = styled.div`
  flex-grow: 1;
`;

export const SendEmailWorkflow = ({ id }) => {
  const { t } = useTranslation('campaigns');
  const shouldShowMissingFieldsWarning = useShouldShowMissingFieldsWarning(id);
  const { getTemplate, getSubjectLine, removeFromFlow, getName } = useSendEmail();
  const { actions, getIsFocused, getIsTouched } = useWorkflowForm();
  const { drawer, openDrawer, closeDrawer } = useDrawer();

  const name = getName(id);

  const removeWorkflow = useCallback(() => {
    closeDrawer();
    removeFromFlow(id);
    actions.setFocused(id, false);
    actions.setTouched(id, false);
  }, [closeDrawer, removeFromFlow, id, actions]);

  const onOpenConfiguration = useCallback(() => {
    openDrawer(id, 'send-email');
    actions.setFocused(id, true);
  }, [openDrawer, id, actions]);

  const template = useMemo(() => getTemplate(id), [getTemplate, id]);
  const subjectLine = useMemo(() => getSubjectLine(id), [getSubjectLine, id]);

  const hasTemplateOrSubjectLineSet = useMemo(
    () =>
      SendEmailTemplateRequiredRule({ template }).result !== 'fail' ||
      SubjectLineRequiredRule({ subjectLine }).result !== 'fail',
    [subjectLine, template],
  );

  const hasSettingsSet = useMemo(() => !!template?.id || (!getIsFocused(id) && getIsTouched(id)), [
    template?.id,
    getIsFocused,
    id,
    getIsTouched,
  ]);

  return (
    <WorkflowBox
      variant={shouldShowMissingFieldsWarning ? 'Incomplete' : 'Default'}
      id={id}
      ariaLabel="Send email workflow"
    >
      <WorkflowBoxHeader
        icon={<SvgIcon name="envelope" size="32" color={theme.designToken.neutral700} />}
        title={t('onsite.editor.workflow.sendEmailMessage.workflow.heading')}
        nameAriaLabel={t('onsite.editor.workflow.sendEmailMessage.workflow.nameAriaLabel')}
        name={name}
        onOpenConfiguration={onOpenConfiguration}
        onRemove={removeWorkflow}
        actionsMenuAriaLabel={t('onsite.editor.workflow.sendEmailMessage.workflow.openConfigurationButtonAria')}
      />
      <WorkflowContent
        showValidationWarning={shouldShowMissingFieldsWarning}
        alertAriaLabel={t('onsite.editor.workflow.sendEmailMessage.workflow.alertIncompleteHeadingAriaLabel')}
      >
        {hasTemplateOrSubjectLineSet ? (
          <SelectedContent>
            <Grow>
              <Box stackItems={true}>
                <WorkflowLabel label="Template Name:" text={template?.name} labelFullWidth />
                {!!subjectLine?.label && (
                  <WorkflowLabel label="Subject Line:" text={subjectLine.label} labelFullWidth />
                )}
              </Box>
            </Grow>
            <Gap right={16} />
            {template?.id && <SendEmailThumbnail id={id} />}
          </SelectedContent>
        ) : (
          <P>{t('onsite.editor.workflow.sendEmailMessage.workflow.defaultBody')}</P>
        )}
      </WorkflowContent>
      <WorkflowBoxFooter
        show={drawer.type === 'send-email' && drawer.id === id && drawer.open}
        hasSettingsSet={hasSettingsSet}
        openConfigurationButtonLabel={t('onsite.editor.workflow.sendEmailMessage.workflow.openConfigurationButton')}
        openConfigurationButtonAriaLabel={t('onsite.editor.workflow.sendEmailMessage.workflow.selectContentButtonAria')}
        onOpenConfiguration={onOpenConfiguration}
      />
    </WorkflowBox>
  );
};
